// components/Ads/AdManager.tsx
import React, { useEffect, useRef, useState } from 'react';
import { AD_CLIENT_ID, getAdSlot } from './adConfig';

type AdType = 'leaderboard' | 'rectangle' | 'native' | 'article' | 'multiplex' | 'text';

interface AdProps {
  type: AdType;
  position: string;
  className?: string;
  context?: 'ai-tools' | 'legal' | 'directory' | 'newsletter';
}

// Global tracking to prevent duplicate pushes
const pushedAds = new Set<string>();

const AdManager: React.FC<AdProps> = ({ 
  type, 
  position, 
  className = '',
  context = 'ai-tools'
}) => {
  const adContainerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const hasInitialized = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const getAdSlotId = () => {
    if (type === 'multiplex') return getAdSlot('multiplex');
    if (type === 'rectangle') return getAdSlot('sidebar');
    if (context === 'newsletter' || position.includes('footer')) return getAdSlot('footer');
    if (position.includes('sidebar')) return getAdSlot('sidebar');
    if (position.includes('article')) return getAdSlot('article');
    if (position.includes('feed')) return getAdSlot('infeed');
    return getAdSlot('header');
  };
  
  // Production ads configuration
  const getAdConfig = () => {
    const baseConfig = {
      leaderboard: {
        style: { display: 'block', height: '90px', width: '100%', maxWidth: '728px' },
        format: 'auto' as const,
        layout: 'horizontal',
        minHeight: '90px'
      },
      rectangle: {
        style: { display: 'block', width: '100%', maxWidth: '300px', height: '250px' },
        format: 'rectangle' as const,
        minHeight: '250px'
      },
      native: {
        style: { display: 'block' },
        format: 'native' as const,
        minHeight: '100px'
      },
      article: {
        style: { display: 'block' },
        format: 'auto' as const,
        minHeight: '100px'
      },
      multiplex: {
        style: { display: 'block' },
        format: 'auto' as const,
        minHeight: '100px'
      },
      text: {
        style: { display: 'inline-block' },
        format: 'text' as const,
        minHeight: '30px'
      }
    };

    return baseConfig[type] || baseConfig.leaderboard;
  };
  
  // Initialize AdSense for production
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      setIsLoading(false);
      return;
    }
    
    if (!adContainerRef.current || hasInitialized.current) return;
    
    // Create unique ad ID
    const adId = `${context}_${position}_${type}`;
    
    // Check if already pushed
    if (pushedAds.has(adId)) {
      console.warn(`Ad ${adId} already pushed - skipping`);
      setIsLoading(false);
      return;
    }
    
    const initializeAd = () => {
      try {
        const container = adContainerRef.current;
        if (!container) return;
        
        const config = getAdConfig();
        const slotId = getAdSlotId();
        const uniqueId = `ad-${adId}-${Date.now()}`;
        let shouldPush = false;
        let createdIns: HTMLElement | null = null;
        
        // If this container already has an AdSense node, do not push again.
        const existingAdNode = container.querySelector('ins.adsbygoogle');
        if (existingAdNode) {
          hasInitialized.current = true;
          pushedAds.add(adId);
          setIsLoading(false);
          return;
        }

        // Don't clear container if it already has content (ad might be loading).
        if (container.children.length === 0) {
          // Create ins element
          const insElement = document.createElement('ins');
          insElement.className = 'adsbygoogle';
          insElement.id = uniqueId;
          Object.assign(insElement.style, config.style);
          
          // Set attributes
          insElement.setAttribute('data-ad-client', AD_CLIENT_ID);
          // AdSense expects a numeric slot id here.
          insElement.setAttribute('data-ad-slot', slotId);
          insElement.setAttribute('data-ad-format', config.format);
          insElement.setAttribute('data-full-width-responsive', 'true');
          
          // Add to container
          container.appendChild(insElement);
          createdIns = insElement;
          shouldPush = true;
        }
        
        // Mark as pushed
        hasInitialized.current = true;
        pushedAds.add(adId);

        // No fresh ad node was inserted, so nothing to push.
        if (!shouldPush) {
          setIsLoading(false);
          return;
        }
        
        // Push to AdSense (with error handling)
        const pushAd = (attempt = 0) => {
          // Guard: avoid calling push when there's no fresh/unfilled ad node.
          if (!createdIns || !createdIns.isConnected) {
            setIsLoading(false);
            return;
          }

          const status = createdIns.getAttribute('data-adsbygoogle-status');
          if (status) {
            // Already filled/processed by AdSense; no need to push again.
            setIsLoading(false);
            return;
          }

          if (window.adsbygoogle) {
            try {
              // Only push if there is at least one unprocessed ad node in DOM.
              const hasUnfilled = !!document.querySelector('ins.adsbygoogle:not([data-adsbygoogle-status])');
              if (!hasUnfilled) {
                setIsLoading(false);
                return;
              }
              (window.adsbygoogle = window.adsbygoogle || []).push({});
              setIsLoading(false);
              console.log('Ad pushed: ' + adId + ' (slot ' + slotId + ')');
            } catch (error) {
              console.warn('Ad push warning for ' + adId + ':', error);
              setHasError(true);
              setIsLoading(false);
            }
            return;
          }

          if (attempt < 5) {
            setTimeout(() => pushAd(attempt + 1), 250);
          } else {
            console.warn('AdSense not ready for ' + adId);
            setHasError(true);
            setIsLoading(false);
          }
        };
        
        // Try pushing
        pushAd();
        
      } catch (error) {
        console.error(`❌ Ad initialization error for ${adId}:`, error);
        setHasError(true);
        setIsLoading(false);
      }
    };
    
    // Use IntersectionObserver for lazy loading
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasInitialized.current) {
          setTimeout(initializeAd, 100);
          if (observerRef.current) {
            observerRef.current.disconnect();
          }
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );
    
    observerRef.current.observe(adContainerRef.current);
    
    // Fallback after 3 seconds
    const timeoutId = setTimeout(() => {
      if (!hasInitialized.current) {
        initializeAd();
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
      }
    }, 3000);
    
    // Cleanup function - DON'T remove ad elements, just disconnect observer
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      clearTimeout(timeoutId);
      // DO NOT remove the ad element - let AdSense manage it
    };
    
  }, [type, position, context]);

  // Development placeholder ads
  const renderPlaceholder = () => {
    const config = getAdConfig();
    
    switch(type) {
      case 'leaderboard':
        return (
          <div 
            className={`ad-leaderboard ${className} h-24 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-dashed border-blue-200 rounded-lg flex items-center justify-center`}
            style={{ minHeight: config.minHeight }}
          >
            <div className="text-center">
              <div className="text-sm text-blue-800 font-medium">Sponsored AI Tool</div>
              <div className="text-xs text-blue-600">728x90 Leaderboard Ad</div>
              <div className="text-xs text-gray-500 mt-1">Slot: {context}_{position}</div>
            </div>
          </div>
        );
        
      case 'rectangle':
        return (
          <div 
            className={`ad-rectangle ${className} h-60 w-full max-w-[300px] bg-gradient-to-b from-purple-50 to-pink-50 border-2 border-dashed border-purple-200 rounded-lg flex items-center justify-center`}
            style={{ minHeight: config.minHeight }}
          >
            <div className="text-center p-4">
              <div className="text-sm text-purple-800 font-medium">Featured Tool</div>
              <div className="text-xs text-purple-600">300x250 Rectangle Ad</div>
              <div className="text-xs text-gray-500 mt-1">Context: {context}</div>
            </div>
          </div>
        );
        
      case 'native':
        return (
          <div 
            className={`ad-native ${className} bg-white border border-gray-200 rounded-xl p-4 shadow-sm`}
            style={{ minHeight: config.minHeight }}
          >
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg flex items-center justify-center">
                <span className="text-lg">🤖</span>
              </div>
              <div className="flex-1">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Sponsored</div>
                <h4 className="text-sm font-medium text-gray-900 mt-1">AI Writing Assistant Pro</h4>
                <p className="text-xs text-gray-600 mt-1">Advanced AI writing tool with 50+ templates</p>
                <button className="mt-2 text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 transition-colors">
                  Try Free
                </button>
              </div>
            </div>
          </div>
        );
        
      case 'article':
        return (
          <div 
            className={`ad-article ${className} my-6 p-4 border-l-4 border-green-500 bg-green-50 rounded-r-lg`}
            style={{ minHeight: config.minHeight }}
          >
            <div className="text-xs text-green-600 uppercase tracking-wide mb-2">Recommended Reading</div>
            <h4 className="font-medium text-gray-900 mb-2">Top 10 AI Tools for 2024</h4>
            <p className="text-sm text-gray-600 mb-3">Discover the most innovative AI solutions in our comprehensive guide.</p>
            <button className="text-sm bg-green-600 text-white px-4 py-1 rounded hover:bg-green-700 transition-colors">
              Read Article
            </button>
          </div>
        );
        
      case 'multiplex':
        return (
          <div 
            className={`ad-multiplex ${className} grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gradient-to-br from-gray-50 to-blue-50 border border-gray-200 rounded-xl`}
            style={{ minHeight: config.minHeight }}
          >
            <div className="bg-white p-3 rounded-lg border border-gray-200">
              <div className="text-xs text-gray-500">AI Tool Spotlight</div>
              <h5 className="text-sm font-medium mt-1">ChatGPT Alternatives</h5>
            </div>
            <div className="bg-white p-3 rounded-lg border border-gray-200">
              <div className="text-xs text-gray-500">Sponsored</div>
              <h5 className="text-sm font-medium mt-1">Midjourney Guide</h5>
            </div>
          </div>
        );
        
      case 'text':
        return (
          <div 
            className={`ad-text ${className} text-center text-xs text-gray-500 border-t border-b border-gray-200 py-2 my-4`}
            style={{ minHeight: config.minHeight }}
          >
            Advertisement: <span className="text-blue-600 hover:underline cursor-pointer">AI Development Services</span> • 
            <span className="text-blue-600 hover:underline cursor-pointer ml-2">Machine Learning Consulting</span>
          </div>
        );
        
      default:
        return (
          <div className={className} style={{ minHeight: config.minHeight }} />
        );
    }
  };
  
  // Production ad container
  const renderProductionAd = () => {
    const config = getAdConfig();
    
    return (
      <div className={`ad-production ${className}`}>
        {/* Ad label */}
        <div className="text-xs text-gray-500 mb-1 text-center">
          Advertisement
        </div>
        
        {/* Ad container - FIXED: Don't manipulate DOM inside React */}
        <div 
          ref={adContainerRef}
          className="ad-container"
          style={{
            width: '100%',
            backgroundColor: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            overflow: 'hidden',
            minHeight: config.minHeight,
            ...(type === 'rectangle' && { maxWidth: '300px', margin: '0 auto' }),
            ...(type === 'leaderboard' && { maxWidth: '728px', margin: '0 auto' }),
          }}
          data-ad-id={`${context}_${position}`}
          data-ad-type={type}
        >
          {/* Loading indicator */}
          {(isLoading || hasError) && (
            <div className="w-full h-full flex items-center justify-center">
              {hasError ? (
                <div className="text-center text-gray-500 text-sm">
                  <div className="text-red-500 mb-1">⚠️ Ad could not load</div>
                  <div className="text-xs">Slot: {context}_{position}</div>
                </div>
              ) : (
                <div className="flex items-center text-gray-500 text-sm">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                  Loading ad...
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };
  
  // Development vs Production
  if (process.env.NODE_ENV === 'development') {
    return renderPlaceholder();
  }
  
  return renderProductionAd();
};

// Helper function to reset pushed ads (for testing)
export const resetPushedAds = () => {
  pushedAds.clear();
  console.log('Ad tracking reset');
};

export default AdManager;

